import { isRouteErrorResponse, redirect } from 'react-router'
import Cookies from 'js-cookie'
import { getJwt, getLogin } from '../api'
import { loginUrl } from '../constants'
import { type TrinityRoles } from '../types'
import { parseFormData } from './objects'

/**
 * Max length for a validated `redirectTo` path (pathname + search).
 * Login URLs also include the path to `/login` and other query params; keeping this well under
 * common ~2000-character URL limits avoids oversized requests and proxy issues after encoding.
 */
const REDIRECT_TO_MAX_LENGTH = 768

/** Drop `redirectTo` if the full login target (absolute or relative) would exceed this size. */
const MAX_LOGIN_REDIRECT_URL_LENGTH = 2000

/**
 * Dummy absolute base for `new URL(relativeLoginPath, …)` only. Relative login paths are not
 * valid URL inputs by themselves; this host is never fetched—it exists solely for parsing.
 */
const INTERNAL_REDIRECT_BASE_URL = 'http://internal.invalid'

const PATH_TRAVERSAL_SEGMENT = /(^|\/)\.\.(?:\/|$)/
const CTRL_OR_DEL_CHAR = /[\u0000-\u001f\u007f]/
const LOOKS_LIKE_URL_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:/

const HTTP_REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308])

function isNavigationRedirect(error: unknown): boolean {
  if (isRouteErrorResponse(error) && HTTP_REDIRECT_STATUSES.has(error.status)) return true
  return error instanceof Response && HTTP_REDIRECT_STATUSES.has(error.status)
}

/**
 * Validates a post-login redirect target. Only same-app relative paths are allowed (open-redirect safe).
 */
export function validateInternalRedirect(raw: string | null | undefined): string | null {
  // Reject missing or non-string values (e.g. tampered form data).
  if (raw == null || typeof raw !== 'string') return null
  const path = raw.trim()
  // Empty after trim, or too long for safe use inside a login URL.
  if (!path || path.length > REDIRECT_TO_MAX_LENGTH) return null
  // Require a single-site path: no scheme, no protocol-relative //host, no relative "foo/bar".
  if (!path.startsWith('/')) return null
  if (path.startsWith('//')) return null
  // Block backslashes (Windows-style paths / URL parser quirks).
  if (path.includes('\\')) return null
  // Block control chars and DEL (header injection / odd parsing).
  if (CTRL_OR_DEL_CHAR.test(path)) return null
  // Reject embedded http:, https:, etc. in the string (open redirect).
  if (path.includes('://')) return null
  // Reject scheme-like prefixes (`javascript:`, `data:`, …). Valid paths here start with `/`, so this matches rarely but keeps parity with the prior guard.
  if (LOOKS_LIKE_URL_SCHEME.test(path)) return null

  const pathOnly = (path.split('?')[0] ?? path).toLowerCase()
  // Reject `/../`, `/..` end, and `../` segments in the pathname only (query may contain `..`).
  if (PATH_TRAVERSAL_SEGMENT.test(pathOnly)) return null
  // Avoid post-login loops back to auth endpoints.
  if (pathOnly === '/login' || pathOnly.startsWith('/login/')) return null
  if (pathOnly === '/logout' || pathOnly.startsWith('/logout/')) return null

  return path
}

function getRequestReturnTo(request?: Request): string | null {
  if (!request) return null
  try {
    const url = new URL(request.url)
    return `${url.pathname}${url.search}`
  } catch {
    return null
  }
}

function buildLoginRedirect(loginPath: string | undefined, request?: Request): string {
  const path = loginPath ?? loginUrl
  const returnTo = getRequestReturnTo(request)
  if (!returnTo) return path

  const validatedReturnTo = validateInternalRedirect(returnTo)
  if (!validatedReturnTo) return path

  const isAbsoluteHttp = path.startsWith('http://') || path.startsWith('https://')

  try {
    const u = isAbsoluteHttp ? new URL(path) : new URL(path, INTERNAL_REDIRECT_BASE_URL)
    u.searchParams.set('redirectTo', validatedReturnTo)
    const result = isAbsoluteHttp ? u.toString() : `${u.pathname}${u.search}`
    if (result.length > MAX_LOGIN_REDIRECT_URL_LENGTH) return path
    return result
  } catch {
    return path
  }
}

/**
 *
 * @param loginPath url to redirect to if not logged in - defaults to Workflow
 * @param request optional route request used to preserve pathname+search for post-login redirect
 * @returns user object
 */
export async function getUser(loginPath?: string, request?: Request) {
  const token = sessionStorage.getItem('jwt')
  const redirectTarget = buildLoginRedirect(loginPath, request)

  if (token) {
    const user = await getLogin()
    return user
  } else {
    console.error('No JWT found')
    throw redirect(redirectTarget)
  }
}

/**
 *
 * @param role 'admin' | 'supplier' | 'dealer'
 * @param loginPath url to redirect to if not logged in - defaults to Workflow
 * @param request optional route request used to preserve pathname+search on login redirect
 * @returns user object
 */
export async function checkAuth(role: TrinityRoles, loginPath?: string, request?: Request) {
  const redirectTarget = buildLoginRedirect(loginPath, request)
  try {
    const user = await getUser(loginPath, request)
    if (user.roles[role]) {
      return user
    } else {
      console.error(`Unauthorized Access - Not a ${role}`)
      sessionStorage.removeItem('jwt')
      Cookies.remove('jwt')
      throw redirect(redirectTarget)
    }
  } catch (error) {
    if (isNavigationRedirect(error)) throw error
    console.error('Unauthorized Access - Not logged in')
    sessionStorage.removeItem('jwt')
    Cookies.remove('jwt')
    throw redirect(redirectTarget)
  }
}

/**
 * Initializes Auth Session token if we have a cookie on first load.
 *
 * This should only be called once during application load
 */
export function initAuth(): void {
  const token = Cookies.get('jwt')
  if (token) {
    sessionStorage.removeItem('jwt')
    sessionStorage.setItem('jwt', token)
  }
}

/**
 *
 * @param loginPath url to redirect to if not logged in - defaults to Workflow
 * @returns redirect to login page
 */
export function logout(loginPath?: string) {
  sessionStorage.removeItem('jwt')
  Cookies.remove('jwt')
  throw redirect(loginPath ?? loginUrl)
}

/**
 *
 * @param request request object from form submission
 * @param role 'admin' | 'supplier' | 'dealer'
 * @param loginPath url to redirect to if not logged in - defaults to Workflow
 * @returns object with properties: error: boolean, data: string containing error message
 */
export async function login(request: Request, role: TrinityRoles, loginPath?: string) {
  const params = await parseFormData(request)
  const response = await getJwt(params)
  if (response.error) {
    return response
  } else {
    const user = await getUser(loginPath)
    if (user.roles[role]) {
      const reqUrl = new URL(request.url)
      const next = validateInternalRedirect(reqUrl.searchParams.get('redirectTo'))
      throw redirect(next ?? '/')
    }
    return { error: true, data: 'User is not authorized to access this application.' }
  }
}
